import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { FavoriteStatusEnum, HistoryListItem } from '@whelk/services/src/type/chat';
import type { TrackParams } from '@whelk/utils';

export const HISTORY_LIST_TYPE = {
  ALL: 'all',
  FAVORITE: 'favorite',
  SEARCH: 'search',
} as const;

export type HistoryListType = (typeof HISTORY_LIST_TYPE)[keyof typeof HISTORY_LIST_TYPE];

interface HistoryListState {
  chatIds: string[];
  isLoading: boolean;
  isPageEnd: boolean;
  isLoadingMore: boolean;
}

// HistoryListState中只保存chatIds，item数据打平到historyItemById中，使用时根据chatId从historyItemById中获取item数据
interface HistoryState {
  historyItemById: Record<string, HistoryListItem>;
  searchResultById: Record<string, HistoryListItem>;
  [HISTORY_LIST_TYPE.ALL]: HistoryListState;
  [HISTORY_LIST_TYPE.FAVORITE]: HistoryListState;
  [HISTORY_LIST_TYPE.SEARCH]: HistoryListState;
  // 批量操作选择状态
  isSelectMode: boolean;
  selectingListType?: HistoryListType;
  selectedChatIds: string[];
  isSelectingAll: boolean;
  // 重命名弹窗
  isHistoryRenameModalOpen: boolean;
  renamingChatId?: string;
  renamingHistoryTrackParams?: TrackParams;
  // 删除弹窗
  isHistoryDeleteModalOpen: boolean;
  deletingChatIds?: string[];
  deletingHistoryTrackParams?: TrackParams;
  // 删除全部弹窗
  isHistoryDeleteAllModalOpen: boolean;
  // 移动端侧边栏
  isOpenMobileHistoryLayer: boolean;
}

const initHistoryList: HistoryListState = {
  chatIds: [],
  isPageEnd: false,
  isLoading: false,
  isLoadingMore: false,
};

const initialState: HistoryState = {
  historyItemById: {},
  searchResultById: {},
  [HISTORY_LIST_TYPE.ALL]: { ...initHistoryList },
  [HISTORY_LIST_TYPE.FAVORITE]: { ...initHistoryList },
  [HISTORY_LIST_TYPE.SEARCH]: { ...initHistoryList },
  // 批量操作选择的对话ID
  isSelectMode: false,
  selectedChatIds: [],
  isSelectingAll: false,
  // 重命名弹窗
  isHistoryRenameModalOpen: false,
  // 删除弹窗
  isHistoryDeleteModalOpen: false,
  // 删除全部弹窗
  isHistoryDeleteAllModalOpen: false,
  // 移动端侧边栏
  isOpenMobileHistoryLayer: false,
};

interface UpdateHistoryTitleParams {
  chatId: string;
  newTitle: string;
}

interface UpdateFavoriteStatusParams {
  chatIds: string[];
  favoriteStatus: FavoriteStatusEnum;
}

interface RenameHistoryModalParams {
  open: boolean;
  chatId?: string;
  trackParams?: TrackParams;
}

const deduplicateHistoryList = (chatIds: string[]) => {
  const resultList = new Map();
  chatIds.forEach((chatId) => {
    resultList.set(chatId, chatId);
  });
  return Array.from(resultList.values());
};

const updateHistoryTitleIfExist = (state: HistoryState, chatId: string, title: string) => {
  // 如果不存在该 chatId，则不更新
  if (!state.historyItemById[chatId]) {
    return state;
  }

  const historyItem = state.historyItemById[chatId];

  return {
    ...state,
    historyItemById: {
      ...state.historyItemById,
      [chatId]: {
        ...historyItem,
        chatTitle: title,
      },
    },
  };
};

const updateHistoryStatusById = (
  itemById: Record<string, HistoryListItem>,
  chatIds: string[],
  status: FavoriteStatusEnum,
) => {
  const containsChatIds = chatIds.filter((chatId) => itemById[chatId]);
  if (containsChatIds.length === 0) {
    return itemById;
  }
  const newHistoryItemById = {
    ...itemById,
  };
  containsChatIds.forEach((chatId) => {
    newHistoryItemById[chatId] = {
      ...itemById[chatId],
      favoriteStatus: status,
    };
  });
  return newHistoryItemById;
};

const updateFavoriteStatus = (
  state: HistoryState,
  chatIds: string[],
  favoriteStatus: FavoriteStatusEnum,
) => {
  const historyItemById = updateHistoryStatusById(state.historyItemById, chatIds, favoriteStatus);
  const searchResultById = updateHistoryStatusById(state.searchResultById, chatIds, favoriteStatus);
  return {
    ...state,
    historyItemById: historyItemById,
    searchResultById: searchResultById,
  };
};

interface UpdateListStateParams {
  chatIds?: string[];
  isPageEnd?: boolean;
  isLoading?: boolean;
  isLoadingMore?: boolean;
}

const updateListStateValue = (
  state: HistoryState,
  listType: HistoryListType,
  params: UpdateListStateParams,
) => {
  return updateListState(state, listType, (_) => params);
};

// 根据历史记录列表类型更新状态，每个HistoryListState中只有chatIds，item数据打平到historyItemById中
const updateListState = (
  state: HistoryState,
  listType: HistoryListType,
  updater: (listState: HistoryListState) => UpdateListStateParams,
) => {
  const listState = state[listType];
  const listStateParams = updater(listState);
  const newListState = {
    ...listState,
    ...listStateParams,
  };
  return {
    ...state,
    [listType]: newListState,
  };
};

// 把历史记录列表数据打平到historyItemById
const updateHistoryItemDict = (state: HistoryState, items: Array<HistoryListItem>) => {
  if (items.length === 0) {
    return state;
  }
  const newHistoryItemById = {
    ...state.historyItemById,
  };
  items.forEach((item) => {
    newHistoryItemById[item.chatID] = item;
  });
  return {
    ...state,
    historyItemById: newHistoryItemById,
  };
};

// 从historyItemById中删除记录
const deleteHistoryItemsById = (state: HistoryState, chatIds: string[]) => {
  const newHistoryItemById = {
    ...state.historyItemById,
  };
  chatIds.forEach((chatId) => {
    delete newHistoryItemById[chatId];
  });
  return {
    ...state,
    historyItemById: newHistoryItemById,
  };
};

// 存放侧边栏 history list
export const historySlice = createSlice({
  name: 'history',
  initialState: initialState,
  reducers: {
    // 修改历史列表
    setHistoryList: (
      state,
      action: PayloadAction<{ listType: HistoryListType; items: HistoryListItem[] }>,
    ) => {
      const { listType, items } = action.payload;
      const chatIds = items.map((item) => item.chatID);
      let newState = updateListStateValue(state, listType, { chatIds });
      newState = updateHistoryItemDict(newState, items);
      return newState;
    },
    setSearchResultList: (state, action: PayloadAction<{ items: HistoryListItem[] }>) => {
      const { items } = action.payload;
      const chatIds = items.map((item) => item.chatID);
      const newState = updateListStateValue(state, HISTORY_LIST_TYPE.SEARCH, { chatIds });
      const searchResultById: Record<string, HistoryListItem> = {};
      items.forEach((item) => {
        searchResultById[item.chatID] = item;
      });
      newState.searchResultById = searchResultById;
      return newState;
    },
    mergeHistoryList: (
      state,
      action: PayloadAction<{ listType: HistoryListType; items: HistoryListItem[] }>,
    ) => {
      const { listType, items } = action.payload;
      const chatIds = items.map((item) => item.chatID);
      let newState = updateListState(state, listType, (listState) => ({
        chatIds: deduplicateHistoryList([...listState.chatIds, ...chatIds]),
      }));
      newState = updateHistoryItemDict(newState, items);
      return newState;
    },
    setIsLoading: (
      state,
      action: PayloadAction<{ listType: HistoryListType; isLoading: boolean }>,
    ) => {
      const { listType, isLoading } = action.payload;
      return updateListStateValue(state, listType, { isLoading });
    },
    setIsLoadingMore: (
      state,
      action: PayloadAction<{ listType: HistoryListType; isLoadingMore: boolean }>,
    ) => {
      const { listType, isLoadingMore } = action.payload;
      return updateListStateValue(state, listType, { isLoadingMore });
    },
    setIsPageEnd: (
      state,
      action: PayloadAction<{ listType: HistoryListType; isPageEnd: boolean }>,
    ) => {
      const { listType, isPageEnd } = action.payload;
      return updateListStateValue(state, listType, { isPageEnd });
    },
    // 批量操作
    setIsSelectMode: (
      state,
      action: PayloadAction<{ isSelectMode: boolean; listType?: HistoryListType }>,
    ) => {
      const { isSelectMode, listType } = action.payload;
      if (isSelectMode && listType) {
        return {
          ...state,
          isSelectMode,
          selectingListType: listType,
        };
      } else {
        return {
          ...state,
          isSelectMode: false,
          selectingListType: undefined,
          selectedChatIds: [],
          isSelectingAll: false,
        };
      }
    },
    setSelectedChatIds: (
      state,
      action: PayloadAction<{ chatIds: string[]; isSelectingAll: boolean }>,
    ) => {
      return {
        ...state,
        selectedChatIds: action.payload.chatIds,
        isSelectingAll: action.payload.isSelectingAll,
      };
    },
    // 删除历史列表
    deleteHistoryListItems: (state, action: PayloadAction<string[]>) => {
      const chatIdSet = new Set(action.payload);
      let newState = state;
      Object.values(HISTORY_LIST_TYPE).forEach((listType) => {
        newState = updateListState(newState, listType, (listState) => ({
          chatIds: listState.chatIds.filter((chatId) => !chatIdSet.has(chatId)),
        }));
      });
      newState = deleteHistoryItemsById(newState, action.payload);
      return newState;
    },
    // 更新历史列表名称
    updateHistoryTitle: (state, action: PayloadAction<UpdateHistoryTitleParams>) => {
      const { chatId, newTitle } = action.payload;
      return updateHistoryTitleIfExist(state, chatId, newTitle);
    },
    // 更新历史列表收藏状态
    updateHistoryFavoriteStatus: (state, action: PayloadAction<UpdateFavoriteStatusParams>) => {
      const { chatIds, favoriteStatus } = action.payload;
      return updateFavoriteStatus(state, chatIds, favoriteStatus);
    },
    // setDeletingHistoryModal: (state, action: PayloadAction<DeleteHistoryModalParams>) => {
    //   const { open, chatIds, trackParams } = action.payload;
    //   return {
    //     ...state,
    //     isHistoryDeleteModalOpen: open,
    //     deletingChatIds: chatIds,
    //     deletingHistoryTrackParams: trackParams,
    //   };
    // },
    setIsHistoryDeleteAllModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isHistoryDeleteAllModalOpen = action.payload;
    },
    setIsOpenMobileHistoryLayer: (state, action: PayloadAction<boolean>) => {
      state.isOpenMobileHistoryLayer = action.payload;
    },
    setRenameHistoryModal: (state, action: PayloadAction<RenameHistoryModalParams>) => {
      const { open, chatId, trackParams } = action.payload;
      return {
        ...state,
        isHistoryRenameModalOpen: open,
        renamingChatId: chatId,
        renamingHistoryTrackParams: trackParams,
      };
    },
    setClearHistoryStore: (_) => {
      return {
        ...initialState,
      };
    },
  },
});

export const {
  setHistoryList,
  setSearchResultList,
  mergeHistoryList,
  setIsLoading,
  setIsLoadingMore,
  setIsPageEnd,
  setIsSelectMode,
  setSelectedChatIds,
  deleteHistoryListItems,
  updateHistoryTitle,
  // setDeletingHistoryModal,
  setRenameHistoryModal,
  setIsOpenMobileHistoryLayer,
  setIsHistoryDeleteAllModalOpen,
  updateHistoryFavoriteStatus,
  setClearHistoryStore,
} = historySlice.actions;

export default historySlice.reducer;
